import { createWithEqualityFn } from 'zustand/traditional';
import type {
  CoinInfoType,
  CoinsMin1Length,
  CurrenciesMin1Length,
  CurrencyInfoType,
} from './types';
import {
  calculateProfitLoss,
  convertAllToBaseCurrencyValue,
  findCoinBySlug,
  findCurrencyById,
} from './utils';

interface CalculatorData {
  currencies: CurrencyInfoType[];
  coins: CoinInfoType[];
  selectedCoin: CoinInfoType | null;
  selectedCurrency: CurrencyInfoType | null;
  sellPrice: string;
  buyPrice: string;
  investment: string;
  investmentFee: string;
  exitFee: string;
  profitLoss: string;
  profitLossDelta: string;
  profitLossPercentage: string;
  totalExitAmount: string;
  totalInvestmentAmount: string;
  state: string;
  totalIncome: string;
  filingStatus: 'single' | 'married';
  holdingPeriod: 'short' | 'long';
  taxAmount: string;
  afterTaxProfit: string;
  taxPercentage: string;
  tradeMode: 'single' | 'multiple';
  totalCryptoGains: string;
}

interface CalculatorStore extends CalculatorData {
  isInit: boolean;
  init: (coins: CoinsMin1Length, currencies: CurrenciesMin1Length) => void;
  setCoins: (coins: CoinsMin1Length) => void;
  setCurrencies: (currencies: CurrenciesMin1Length) => void;
  setSelectedCoin: (coin: CoinInfoType) => void;
  setSelectedCurrency: (currency: CurrencyInfoType) => void;
  setSellPrice: (value: string) => void;
  setBuyPrice: (value: string) => void;
  setInvestment: (value: string) => void;
  setInvestmentFee: (value: string) => void;
  setExitFee: (value: string) => void;
  setState: (value: string) => void;
  setTotalIncome: (value: string) => void;
  setFilingStatus: (value: 'single' | 'married') => void;
  setHoldingPeriod: (value: 'short' | 'long') => void;
  setTradeMode: (value: 'single' | 'multiple') => void;
  setTotalCryptoGains: (value: string) => void;
}

interface InitialDataProps {
  coins: CoinsMin1Length;
  currencies: CurrenciesMin1Length;
}

function getInitData({ coins, currencies }: InitialDataProps): CalculatorData {
  const initialSelectedCoin = coins[0] as CoinInfoType;
  const initialSelectedCurrency = currencies[0] as CurrencyInfoType;
  const initialStatePriceInputs = convertAllToBaseCurrencyValue(
    {
      buyPrice: initialSelectedCoin.priceUsd,
      sellPrice: initialSelectedCoin.priceUsd,
      investment: 0,
      investmentFee: 0,
      exitFee: 0,
      totalCryptoGains: 0,
    },
    initialSelectedCurrency,
  );
  const initialTaxInputs = convertAllToBaseCurrencyValue(
    {
      totalIncome: '0',
      taxAmount: '0',
      afterTaxProfit: '0',
      taxPercentage: '0',
    },
    initialSelectedCurrency,
  );
  const initialStateInputs = {
    selectedCoin: initialSelectedCoin,
    selectedCurrency: initialSelectedCurrency,
    ...(initialStatePriceInputs as {
      buyPrice: string;
      sellPrice: string;
      investment: string;
      investmentFee: string;
      exitFee: string;
      totalCryptoGains: string;
    }),
    ...(initialTaxInputs as {
      totalIncome: string;
      taxAmount: string;
      afterTaxProfit: string;
      taxPercentage: string;
    }),
  };

  const initialStateProfitAndLoss = calculateProfitLoss({
    ...initialStateInputs,
  });
  return {
    coins,
    currencies,
    ...initialStateInputs,
    ...initialStateProfitAndLoss,
    state: '',
    filingStatus: 'single',
    holdingPeriod: 'short',
    tradeMode: 'single',
  };
}

// Updated for Zustand v5 type-safety
type SetState = (
  partial:
    | CalculatorStore
    | Partial<CalculatorStore>
    | ((state: CalculatorStore) => CalculatorStore | Partial<CalculatorStore>),
  replace?: false | undefined,
) => void;

// Generic function to create a slice with a value and its setter
const createValueSlice =
  <K extends keyof CalculatorStore>(
    key: K,
    defaultValue: CalculatorStore[K] = '0' as CalculatorStore[K],
  ) =>
  (set: SetState) => {
    type SetterKey = `set${Capitalize<string & K>}`;
    type SliceType = {
      [P in K]: CalculatorStore[P];
    } & {
      [P in SetterKey]: (value: CalculatorStore[K]) => void;
    };

    return {
      [key]: defaultValue,
      [`set${key.charAt(0).toUpperCase()}${key.slice(1)}`]: (
        value: CalculatorStore[K],
      ) =>
        set((state: CalculatorStore) => ({
          [key]: value,
          ...calculateProfitLoss({
            ...state,
            [key]: value,
            state: state.state,
            totalIncome: state.totalIncome,
            filingStatus: state.filingStatus,
            holdingPeriod: state.holdingPeriod,
          }),
        })),
    } as SliceType;
  };

const createCurrencySlice = (set: SetState) => ({
  currencies: [] as CurrencyInfoType[],
  selectedCurrency: null,
  setSelectedCurrency: (selectedCurrency: CurrencyInfoType) =>
    set((state: CalculatorStore) => {
      const updatedValues = convertAllToBaseCurrencyValue(
        {
          buyPrice: state?.selectedCoin?.priceUsd ?? 0,
          sellPrice: state?.selectedCoin?.priceUsd ?? 0,
          investment: 0,
          investmentFee: 0,
          exitFee: 0,
        },
        selectedCurrency,
      );
      const profitAndLossUpdates = calculateProfitLoss({
        selectedCurrency: selectedCurrency,
        ...(updatedValues as {
          buyPrice: string;
          sellPrice: string;
          investment: string;
          investmentFee: string;
          exitFee: string;
        }),
      });
      return {
        ...updatedValues,
        ...profitAndLossUpdates,
        selectedCurrency: selectedCurrency,
      };
    }),
  setCurrencies: (currencies: CurrencyInfoType[]) =>
    set((state: CalculatorStore) => {
      if (findCurrencyById(currencies, state?.selectedCurrency?.id)) {
        return { currencies };
      } else {
        const selectedCurrency = currencies[0] as CurrencyInfoType;
        const updatedValues = convertAllToBaseCurrencyValue(
          {
            buyPrice: state?.selectedCoin?.priceUsd ?? 0,
            sellPrice: state?.selectedCoin?.priceUsd ?? 0,
            investment: 0,
            investmentFee: 0,
            exitFee: 0,
          },
          selectedCurrency,
        );
        const profitAndLossUpdates = calculateProfitLoss({
          selectedCurrency,
          ...(updatedValues as {
            buyPrice: string;
            sellPrice: string;
            investment: string;
            investmentFee: string;
            exitFee: string;
          }),
        });
        return {
          ...updatedValues,
          ...profitAndLossUpdates,
          currencies,
          selectedCurrency,
        };
      }
    }),
});

const createCoinSlice = (set: SetState) => ({
  coins: [] as CoinInfoType[],
  selectedCoin: null,
  setSelectedCoin: (selectedCoin: CoinInfoType) =>
    set((state: CalculatorStore) => {
      const newPricing = convertAllToBaseCurrencyValue(
        {
          buyPrice: selectedCoin.priceUsd,
          sellPrice: selectedCoin.priceUsd,
        },
        state?.selectedCurrency ?? undefined,
      );

      const profitAndLossUpdates = calculateProfitLoss({
        ...state,
        ...(newPricing as { buyPrice: string; sellPrice: string }),
      });
      return {
        ...newPricing,
        ...profitAndLossUpdates,
        selectedCoin,
      };
    }),
  setCoins: (coins: CoinInfoType[]) =>
    set((state: CalculatorStore) => {
      const selectedCoin =
        findCoinBySlug(coins, state?.selectedCoin?.slug) ??
        (coins[0] as CoinInfoType);
      const newPricing = convertAllToBaseCurrencyValue(
        {
          buyPrice: selectedCoin.priceUsd,
          sellPrice: selectedCoin.priceUsd,
        },
        state?.selectedCurrency ?? undefined,
      );
      const profitAndLossUpdates = calculateProfitLoss({
        ...state,
        ...(newPricing as { buyPrice: string; sellPrice: string }),
      });
      return {
        coins,
        ...newPricing,
        ...profitAndLossUpdates,
        selectedCoin,
      };
    }),
});

const createBuyPriceSlice = createValueSlice('buyPrice');
const createSellPriceSlice = createValueSlice('sellPrice');
const createInvestmentSlice = createValueSlice('investment');
const createInvestmentFeeSlice = createValueSlice('investmentFee');
const createExitFeeSlice = createValueSlice('exitFee');

const createProfitLossAndTaxSlice = (set: SetState) => ({
  profitLoss: '0',
  profitLossDelta: '0',
  profitLossPercentage: '0',
  totalExitAmount: '0',
  totalInvestmentAmount: '0',
  taxPercentage: '0',

  state: '',
  totalIncome: '0',
  filingStatus: 'single' as const,
  holdingPeriod: 'short' as const,
  taxAmount: '0',
  afterTaxProfit: '0',

  setState: (newState: string) =>
    set((state: CalculatorStore) => ({
      state: newState,
      ...calculateProfitLoss({
        ...state,
        state: newState,
        totalIncome: state.totalIncome,
        filingStatus: state.filingStatus,
        holdingPeriod: state.holdingPeriod,
      }),
    })),
  setTotalIncome: (totalIncome: string) =>
    set((state: CalculatorStore) => ({
      totalIncome,
      ...calculateProfitLoss({
        ...state,
        totalIncome,
        state: state.state,
        filingStatus: state.filingStatus,
        holdingPeriod: state.holdingPeriod,
      }),
    })),
  setFilingStatus: (filingStatus: 'single' | 'married') =>
    set((state: CalculatorStore) => ({
      filingStatus,
      ...calculateProfitLoss({
        ...state,
        filingStatus,
        state: state.state,
        totalIncome: state.totalIncome,
        holdingPeriod: state.holdingPeriod,
      }),
    })),
  setHoldingPeriod: (holdingPeriod: 'short' | 'long') =>
    set((state: CalculatorStore) => ({
      holdingPeriod,
      ...calculateProfitLoss({
        ...state,
        holdingPeriod,
        state: state.state,
        totalIncome: state.totalIncome,
        filingStatus: state.filingStatus,
      }),
    })),
});

const createTradeModeSlice = createValueSlice('tradeMode', 'single');
const createTotalCryptoGainsSlice = createValueSlice('totalCryptoGains');

const createInitSlice = (set: SetState) => ({
  isInit: false,
  init: (coins: CoinsMin1Length, currencies: CurrenciesMin1Length) =>
    set(() => {
      const calculatorData = getInitData({ coins, currencies });
      return {
        ...calculatorData,
        isInit: true,
      };
    }),
});

export const useBoundCalculatorStore = createWithEqualityFn<CalculatorStore>(
  (setState) => ({
    ...createInitSlice(setState),
    ...createCoinSlice(setState),
    ...createCurrencySlice(setState),
    ...createBuyPriceSlice(setState),
    ...createSellPriceSlice(setState),
    ...createInvestmentSlice(setState),
    ...createInvestmentFeeSlice(setState),
    ...createExitFeeSlice(setState),
    ...createProfitLossAndTaxSlice(setState),
    ...createTradeModeSlice(setState),
    ...createTotalCryptoGainsSlice(setState),
  }),
  Object.is,
);
